import { Box, Typography, styled } from '@mui/material';

import {
  CALENDAR_COLUMNS_TEMPLATE,
  CALENDAR_HEADER_HEIGHT,
} from '../../components/CalendarDayHeader/CalendarDayHeader.styles';
import {
  CALENDAR_GRID_WIDTH_COMPENSATION,
  CALENDAR_PANEL_BASE_STYLES,
  CALENDAR_SCROLL_CONTENT_SPACING,
} from '../../shared/Calendar.layout';

export const MonthGridWrapper = styled(Box)(() => ({
  ...CALENDAR_PANEL_BASE_STYLES,
  display: 'grid',
  gridTemplateRows: `${CALENDAR_HEADER_HEIGHT} minmax(0, 1fr)`,
  width: '100%',
  alignItems: 'stretch',
  overflow: 'hidden',
  position: 'relative',
}));

export const MonthBodyScrollArea = styled(Box)(() => ({
  position: 'relative',
  minHeight: 0,
  overflow: 'hidden',
}));

export const MonthBodyScroller = styled(Box)(() => ({
  position: 'absolute',
  inset: 0,
  ...CALENDAR_SCROLL_CONTENT_SPACING,
  overflowY: 'auto',
  overflowX: 'hidden',
  scrollbarWidth: 'none',
  msOverflowStyle: 'none',
  '&::-webkit-scrollbar': {
    display: 'none',
  },
}));

export const MonthGrid = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'weeksCount',
})<{ weeksCount: number }>(({ weeksCount }) => ({
  display: 'grid',
  height: '100%',
  minHeight: 0,
  alignSelf: 'stretch',
  alignContent: 'stretch',
  gridTemplateRows: `repeat(${weeksCount}, minmax(0,1fr))`,
  gridTemplateColumns: CALENDAR_COLUMNS_TEMPLATE,
  width: CALENDAR_GRID_WIDTH_COMPENSATION,
}));

export const MonthDayCellBox = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== 'isCurrentMonth' && prop !== 'isFirstRow' && prop !== 'isToday',
})<{ isCurrentMonth: boolean; isFirstRow?: boolean; isToday?: boolean }>(
  ({ isCurrentMonth, isFirstRow, isToday, theme }) => ({
    display: 'flex',
    boxSizing: 'border-box',
    flexDirection: 'column',
    padding: '2.5625rem 0.625rem 0.375rem 0.625rem',
    gap: '0.3125rem',
    alignItems: 'flex-start',
    alignSelf: 'stretch',
    justifySelf: 'stretch',
    background: isCurrentMonth ? 'var(--Background-1, #FEFEFE)' : 'var(--Background-2, #F5F5F7)',
    borderTop: isFirstRow ? '1px solid var(--Gray-200, #DBDBE2)' : 'none',
    borderRight: '1px solid var(--Gray-200, #DBDBE2)',
    borderBottom: '1px solid var(--Gray-200, #DBDBE2)',
    borderLeft: 'none',
    position: 'relative',
    ...(isToday && {
      outline: `2px solid ${theme.palette.accent.main}`,
      outlineOffset: '-2px',
      borderRadius: '0.25rem',
      zIndex: 1,
    }),
    '&:hover': {
      background: isCurrentMonth ? '#F5F5F7' : '#EBEAEF',
    },
    '&:nth-of-type(7n)': {
      borderRight: 'none',
    },
  }),
);

export const MonthDayNumberWrapper = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '0.625rem',
  position: 'absolute',
  top: '0.625rem',
  left: '0.625rem',
}));

export const MonthDayNumberText = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'isToday',
})<{ isToday?: boolean }>(({ isToday, theme }) => ({
  color: isToday ? theme.palette.accent.main : 'var(--Typography-h-secondary, #504E5A)',
  fontFamily: '"Neue Frutiger World"',
  fontSize: '1.25rem',
  fontStyle: 'normal',
  fontWeight: 500,
  lineHeight: '120%',
}));

export const Row = styled(Box)(() => ({
  display: 'grid',
  gridTemplateColumns: CALENDAR_COLUMNS_TEMPLATE,
  width: '100%',
}));

export const HeaderCellYear = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '0.5rem 0.25rem',
  color: 'var(--Typography-h-secondary, #504E5A)',
  fontFamily: '"Neue Frutiger World"',
  fontSize: '0.8125rem',
  fontStyle: 'normal',
  fontWeight: 500,
  lineHeight: '120%',
  textTransform: 'capitalize',
}));
